import { IsoCountryCode2Char } from '@rbilabs/intl-common';

import { Channel } from 'src/graphql';

import { roundTo } from './pricing';

type IUpdateMenuFromCSVInput = {
  serviceMode: string;
  plu: string;
  availability?: boolean;
  price?: number;
  lateNightPrice?: number;
  lateNightAvailability?: boolean;
};

type IStore = {
  [key: string]: {
    channel: Channel;
    items: IUpdateMenuFromCSVInput[];
  };
};

type IGroup = IStore;

type OldIUpdateMenuFromCSVInput = {
  plu: string;
  price: number;
};

type OldIStore = {
  [key: string]: OldIUpdateMenuFromCSVInput[];
};

export class CsvRowValidationError extends Error {
  messageId: string;
  row: number;

  constructor(message: string, messageId: string, row: number) {
    super(message);
    this.messageId = messageId;
    this.row = row;
  }
}

// CSV parser function for uploading prices
export const parseCsv = (csv: string, channel: Channel) => {
  const lines: string[] = csv.split('\n').map(columnName => columnName.replace(/[\n\r]/g, ''));
  const stores: IStore[] = [];
  const groups: IGroup[] = [];
  lines.forEach((line: string, index: number) => {
    const lineCells = line.split(',');
    if (!index || lineCells.length === 1) {
      return;
    }

    const [
      storeId,
      plu,
      price,
      availability,
      serviceMode,
      ,
      ,
      lateNightPrice,
      lateNightAvailability,
      storeGroupId,
    ] = lineCells;

    if (!!storeId === !!storeGroupId) {
      throw new CsvRowValidationError(
        'StoreId and GroupId is configured',
        'upload-prices.storeAndGroupError',
        index
      );
    }

    const isMalformed = (val?: string) => val?.includes(',') || val?.includes('.');

    if ([price, lateNightPrice].some(isMalformed)) {
      throw new CsvRowValidationError(
        'The price should be specified in cents',
        'upload-prices.priceError',
        index
      );
    }

    const storeData: IUpdateMenuFromCSVInput = {
      serviceMode,
      plu,
    };

    const parsedPrice = Number.parseFloat(price);
    const parsedAvailability = Number(availability);
    const parsedLateNightPrice = Number.parseFloat(lateNightPrice);
    const parsedLateNightAvailability = Number(lateNightAvailability);
    const isValidPrice = (_val: number): boolean => {
      return !Number.isNaN(_val) && _val >= 0;
    };

    // Track if any valid modifications are being made
    let hasValidModification = false;

    if (isValidPrice(parsedPrice)) {
      storeData.price = roundTo(parsedPrice, 0);
      hasValidModification = true;
    }

    if (isValidPrice(parsedLateNightPrice)) {
      storeData.lateNightPrice = roundTo(parsedLateNightPrice, 0);
      hasValidModification = true;
    }

    if (availability !== '' && !Number.isNaN(parsedAvailability)) {
      storeData.availability = Boolean(parsedAvailability);
      hasValidModification = true;
    }

    if (lateNightAvailability !== '' && !Number.isNaN(parsedLateNightAvailability)) {
      storeData.lateNightAvailability = Boolean(parsedLateNightAvailability);
      hasValidModification = true;
    }

    // Throw error if no valid modifications are present
    if (!hasValidModification) {
      throw new CsvRowValidationError(
        'No valid price or availability modifications found. At least one of price, availability, lateNightPrice, or lateNightAvailability must be specified.',
        'upload-prices.noValidModifications',
        index
      );
    }

    if (storeGroupId?.trim()) {
      const groupIndex = groups.findIndex(group => group[storeGroupId]);
      if (groupIndex !== -1) {
        groups[groupIndex][storeGroupId].items.push(storeData);
      } else {
        groups.push({ [storeGroupId]: { channel, items: [storeData] } });
      }
    }

    if (!storeId.trim()) {
      return;
    }

    const storeIndex = stores.findIndex(store => store[storeId]);

    if (storeIndex !== -1) {
      stores[storeIndex][storeId].items.push(storeData);
      return;
    }

    stores.push({ [storeId]: { channel, items: [storeData] } });
  });

  return [stores, groups];
};

// Old CSV parser function for uploading prices
export const parseOldCsv = (csv: string) => {
  const lines: string[] = csv.split('\n');
  let stores: OldIStore[] = [];

  lines.forEach((line: string, index: number) => {
    const lineCells = line.split(',');

    // Handle CSV file headers. The first two columns should be Item Name and PLU
    // The remaining columns should be the store IDs that are getting updated
    if (!index) {
      const [, , ...storeIds] = lineCells;
      stores = storeIds.map(storeId => ({ [Number.parseInt(storeId, 10)]: [] }));
      return;
    }

    // Handle the rows containing items
    const [, itemPlu, ...prices] = lineCells;

    prices.forEach((price: string, i: number) => {
      const storeId: string = Object.keys(stores[i])[0];
      const parsedPrice = Number.parseFloat(price) * 100;

      // If no price was entered or negative we skip the item (0 is a valid price)
      if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
        return;
      }

      stores[i][storeId].push({
        plu: itemPlu,
        price: roundTo(parsedPrice, 0),
      });
    });
  });

  return stores;
};

/**
 * Download CSV helper
 * Creates a blob from a csv text and makes initiates the browser download
 * by creating a anchor element and triggering a click event
 */
export const downloadCSV = function (filename: string, data: string): void {
  // Creating a Blob for having a csv file format
  // and passing the data with type
  const blob = new Blob([data], { type: 'text/csv' });

  // Creating an object for downloading url
  const url = window.URL.createObjectURL(blob);

  // Creating an anchor(a) tag of HTML
  const a = document.createElement('a');

  // Passing the blob downloading url
  a.setAttribute('href', url);

  // Setting the anchor tag attribute for downloading
  // and passing the download file name
  a.setAttribute('download', filename);

  // Performing a download with click
  a.click();
};

interface FetchCsvParams {
  url: string;
  body: string;
  token: string;
  region: IsoCountryCode2Char;
  headers?: any;
}

/**
 * CSV Download fetcher function
 */
export const fetchCsv = async ({
  url,
  body,
  token,
  region,
  headers,
}: FetchCsvParams): Promise<void> => {
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'text/csv',
      'okta-id-token': token ?? '',
      'x-ui-region': region,
      ...headers,
    },
    body,
  });

  if (!res.ok || (res.status >= 400 && res.status < 600)) {
    return Promise.reject(new Error(`CSV fetch failed with status: ${res.status}`));
  }

  const filename =
    res.headers.get('content-disposition')?.split('=')[1] ?? `Report_${Date.now()}.csv`;
  const csv = await res.text();

  downloadCSV(filename, csv);

  return Promise.resolve();
};
